// Catalog of curated Google Fonts for invitations.
// All these are loaded in /public/index.html for immediate preview.
import { rgbChannels, hexToRgb } from "@/lib/palette";

function darkenHex(hex, amount) {
  const c = hexToRgb(hex);
  if (!c) return hex;
  const f = (v) => Math.max(0, Math.min(255, Math.round(v * (1 - amount))));
  const to = (v) => f(v).toString(16).padStart(2, "0");
  return `#${to(c.r)}${to(c.g)}${to(c.b)}`;
}

export const HEADLINE_FONTS = [
  { id: "Playfair Display", label: "Playfair Display", css: "'Playfair Display', serif" },
  { id: "Cormorant Garamond", label: "Cormorant", css: "'Cormorant Garamond', serif" },
  { id: "Fraunces", label: "Fraunces", css: "'Fraunces', serif" },
  { id: "Italiana", label: "Italiana", css: "'Italiana', serif" },
  { id: "Bodoni Moda", label: "Bodoni Moda", css: "'Bodoni Moda', serif" },
  { id: "Marcellus", label: "Marcellus", css: "'Marcellus', serif" },
  { id: "Prata", label: "Prata", css: "'Prata', serif" },
  { id: "DM Serif Display", label: "DM Serif", css: "'DM Serif Display', serif" },
  { id: "Cinzel", label: "Cinzel", css: "'Cinzel', serif" },
  { id: "Libre Baskerville", label: "Libre Baskerville", css: "'Libre Baskerville', serif" },
  { id: "Cardo", label: "Cardo", css: "'Cardo', serif" },
  { id: "EB Garamond", label: "EB Garamond", css: "'EB Garamond', serif" },
  { id: "Lora", label: "Lora", css: "'Lora', serif" },
];

export const SCRIPT_FONTS = [
  { id: "Dancing Script", label: "Dancing Script", css: "'Dancing Script', cursive" },
  { id: "Great Vibes", label: "Great Vibes", css: "'Great Vibes', cursive" },
  { id: "Allura", label: "Allura", css: "'Allura', cursive" },
  { id: "Parisienne", label: "Parisienne", css: "'Parisienne', cursive" },
  { id: "Sacramento", label: "Sacramento", css: "'Sacramento', cursive" },
  { id: "Pinyon Script", label: "Pinyon", css: "'Pinyon Script', cursive" },
  { id: "Alex Brush", label: "Alex Brush", css: "'Alex Brush', cursive" },
  { id: "Petit Formal Script", label: "Petit Formal", css: "'Petit Formal Script', cursive" },
  { id: "Tangerine", label: "Tangerine", css: "'Tangerine', cursive" },
  { id: "Yellowtail", label: "Yellowtail", css: "'Yellowtail', cursive" },
  { id: "Herr Von Muellerhoff", label: "Herr Von M.", css: "'Herr Von Muellerhoff', cursive" },
  { id: "Meddon", label: "Meddon", css: "'Meddon', cursive" },
  { id: "Rouge Script", label: "Rouge", css: "'Rouge Script', cursive" },
];

export const BODY_FONTS = [
  { id: "Outfit", label: "Outfit", css: "'Outfit', sans-serif" },
  { id: "Inter", label: "Inter", css: "'Inter', sans-serif" },
  { id: "Nunito", label: "Nunito", css: "'Nunito', sans-serif" },
  { id: "Karla", label: "Karla", css: "'Karla', sans-serif" },
  { id: "Jost", label: "Jost", css: "'Jost', sans-serif" },
  { id: "Quicksand", label: "Quicksand", css: "'Quicksand', sans-serif" },
  { id: "Poppins", label: "Poppins", css: "'Poppins', sans-serif" },
  { id: "Manrope", label: "Manrope", css: "'Manrope', sans-serif" },
  { id: "Work Sans", label: "Work Sans", css: "'Work Sans', sans-serif" },
  { id: "DM Sans", label: "DM Sans", css: "'DM Sans', sans-serif" },
  { id: "Raleway", label: "Raleway", css: "'Raleway', sans-serif" },
];

export const TITLE_SIZES = [
  { id: "xs", label: "XS", scale: 0.74 },
  { id: "sm", label: "S", scale: 0.89 },
  { id: "md", label: "M", scale: 1.05 },
  { id: "lg", label: "L", scale: 1.21 },
  { id: "xl", label: "XL", scale: 1.42 },
  { id: "xxl", label: "XXL", scale: 1.63 },
];

// Same size scale is reused for hosts and subtitle so all three feel consistent.
export const HOSTS_SIZES = TITLE_SIZES;

// Body text uses a gentler scale so paragraphs stay readable at every step.
export const BODY_SIZES = [
  { id: "xs", label: "XS", scale: 0.89 },
  { id: "sm", label: "S", scale: 0.97 },
  { id: "md", label: "M", scale: 1.05 },
  { id: "lg", label: "L", scale: 1.16 },
  { id: "xl", label: "XL", scale: 1.28 },
  { id: "xxl", label: "XXL", scale: 1.42 },
];
export const SUBTITLE_SIZES = TITLE_SIZES;

export function findFont(list, id) {
  return list.find((f) => f.id === id) || null;
}

// Build a style object that InvitationView can inject as CSS variables
export function buildStyleVars(customStyle) {
  const cs = customStyle || {};
  const style = {};
  const list = cs.custom_fonts || [];
  const findCustom = (cat, id) => list.find((f) => f && f.category === cat && f.id === id);
  // Unified search: allows message_font and hosts_font to be any font from any category
  const ALL_FONTS = [...HEADLINE_FONTS, ...SCRIPT_FONTS, ...BODY_FONTS];
  const h = findFont(HEADLINE_FONTS, cs.headline_font);
  const s = findFont(SCRIPT_FONTS, cs.script_font);
  const b = findFont(BODY_FONTS, cs.body_font);
  const hostsBuilt = findFont(ALL_FONTS, cs.hosts_font);
  if (h) style["--fn-headline"] = h.css;
  else {
    const cH = findCustom("headline", cs.headline_font);
    if (cH) style["--fn-headline"] = `'${cH.family}', serif`;
  }
  if (s) style["--fn-script"] = s.css;
  else {
    const cS = findCustom("script", cs.script_font);
    if (cS) style["--fn-script"] = `'${cS.family}', cursive`;
  }
  if (b) style["--fn-body"] = b.css;
  else {
    const cB = findCustom("body", cs.body_font);
    if (cB) style["--fn-body"] = `'${cB.family}', sans-serif`;
  }
  if (hostsBuilt) style["--fn-hosts-font"] = hostsBuilt.css;
  else {
    const cHosts = findCustom("hosts", cs.hosts_font);
    if (cHosts) style["--fn-hosts-font"] = `'${cHosts.family}', sans-serif`;
  }
  const messageBuilt = findFont(ALL_FONTS, cs.message_font);
  if (messageBuilt) style["--fn-message-font"] = messageBuilt.css;
  else {
    const cMsg = findCustom("message", cs.message_font);
    if (cMsg) style["--fn-message-font"] = `'${cMsg.family}', serif`;
  }
  const themeLabelBuilt = findFont(ALL_FONTS, cs.theme_label_font);
  if (themeLabelBuilt) style["--fn-theme-label-font"] = themeLabelBuilt.css;
  else {
    const cLbl = findCustom("theme_label", cs.theme_label_font);
    if (cLbl) style["--fn-theme-label-font"] = `'${cLbl.family}', sans-serif`;
  }
  if (cs.text_color) style["--fn-ink"] = cs.text_color;
  if (cs.accent_color) style["--fn-warmgold"] = cs.accent_color;
  if (cs.subtitle_color) style["--fn-subtitle-color"] = cs.subtitle_color;
  // RGB channel vars: re-theme every Tailwind token (including /opacity variants)
  const setChan = (name, hex) => { const c = hex && rgbChannels(hex); if (c) style[name] = c; };
  setChan("--c-ink", cs.text_color);
  setChan("--c-warmgold", cs.accent_color);
  setChan("--c-warmgold-dark", cs.accent_color ? darkenHex(cs.accent_color, 0.14) : null);
  setChan("--c-cream", cs.bg_color);
  setChan("--c-sand", cs.surface_color);
  setChan("--c-inksoft", cs.muted_color);
  // Numeric fine-tune scale takes precedence over the preset id when set (>0).
  const resolveScale = (scaleVal, sizeId, sizeList) => {
    const n = Number(scaleVal);
    if (scaleVal != null && scaleVal !== "" && !Number.isNaN(n) && n > 0) return String(n);
    const found = sizeList.find((t) => t.id === sizeId);
    return found ? String(found.scale) : null;
  };
  const bodyScale = resolveScale(cs.body_scale, cs.body_size, BODY_SIZES);
  if (bodyScale) style["--fn-body-scale"] = bodyScale;
  const titleScale = resolveScale(cs.title_scale, cs.title_size, TITLE_SIZES);
  if (titleScale) style["--fn-title-scale"] = titleScale;
  const hostsScale = resolveScale(cs.hosts_scale, cs.hosts_size, HOSTS_SIZES);
  if (hostsScale) style["--fn-hosts-scale"] = hostsScale;
  const subtitleScale = resolveScale(cs.subtitle_scale, cs.subtitle_size, SUBTITLE_SIZES);
  if (subtitleScale) style["--fn-subtitle-scale"] = subtitleScale;
  const themeLabelScale = resolveScale(cs.theme_label_scale, cs.theme_label_size, SUBTITLE_SIZES);
  if (themeLabelScale) style["--fn-theme-label-scale"] = themeLabelScale;
  const messageScale = resolveScale(cs.message_scale, cs.message_size, TITLE_SIZES);
  if (messageScale) style["--fn-message-scale"] = messageScale;
  return style;
}
